import { defineMessages, useIntl } from 'react-intl';
import type { ReviewableResourceType } from './useUnreviewedExtractedResources';

/**
 * Clinician-friendly labels for the reviewable extracted resource types. Shared
 * between the Zori Pulse review queue (`useExtractedResourceReview`) and the
 * "Analyze with Zori AI" extraction modal so the same resource reads
 * consistently wherever it is surfaced.
 */
const reviewTypeMessages = defineMessages({
  reviewTypeCondition: {
    id: 'MemberDetail.extractedReviewTypeCondition',
    defaultMessage: 'Condition',
  },
  reviewTypeObservation: {
    id: 'MemberDetail.extractedReviewTypeObservation',
    defaultMessage: 'Lab',
  },
  reviewTypeMedicationStatement: {
    id: 'MemberDetail.extractedReviewTypeMedicationStatement',
    defaultMessage: 'Medication',
  },
  reviewTypeAllergyIntolerance: {
    id: 'MemberDetail.extractedReviewTypeAllergyIntolerance',
    defaultMessage: 'Allergy',
  },
  reviewTypeImmunization: {
    id: 'MemberDetail.extractedReviewTypeImmunization',
    defaultMessage: 'Immunization',
  },
  reviewTypeProcedure: {
    id: 'MemberDetail.extractedReviewTypeProcedure',
    defaultMessage: 'Procedure',
  },
});

const REVIEW_TYPE_MESSAGE_IDS: Record<
  ReviewableResourceType,
  keyof typeof reviewTypeMessages
> = {
  Condition: 'reviewTypeCondition',
  Observation: 'reviewTypeObservation',
  MedicationStatement: 'reviewTypeMedicationStatement',
  AllergyIntolerance: 'reviewTypeAllergyIntolerance',
  Immunization: 'reviewTypeImmunization',
  Procedure: 'reviewTypeProcedure',
};

export const REVIEWABLE_RESOURCE_TYPES: ReadonlySet<string> = new Set(
  Object.keys(REVIEW_TYPE_MESSAGE_IDS),
);

/**
 * Returns a memo-stable resolver from a `ReviewableResourceType` to its
 * clinician-friendly label (e.g. `MedicationStatement` -> "Medication").
 */
export const useReviewableResourceTypeLabel = () => {
  const { formatMessage } = useIntl();
  return (resourceType: ReviewableResourceType): string =>
    formatMessage(reviewTypeMessages[REVIEW_TYPE_MESSAGE_IDS[resourceType]]);
};
